import { ReactNode, useCallback, useEffect, useState } from "react";
import {
  Info,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  X,
  LucideIcon,
} from "lucide-react";
import { ToastCtx, type Toast, type ToastVariant } from "./toastContext";
import styles from "./Toast.module.css";

const ICONS: Record<ToastVariant, LucideIcon> = {
  info: Info,
  success: CheckCircle2,
  warn: AlertTriangle,
  danger: AlertCircle,
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((t: Omit<Toast, "id">) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((prev) => [...prev, { ...t, id }]);
  }, []);
  function dismiss(id: string) {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }
  return (
    <ToastCtx.Provider value={{ push }}>
      {children}
      <div className={styles.stack}>
        {toasts.map((t) => (
          <ToastItem key={t.id} t={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

function ToastItem({ t, onDismiss }: { t: Toast; onDismiss: () => void }) {
  useEffect(() => {
    const h = setTimeout(onDismiss, 5000);
    return () => clearTimeout(h);
  }, [onDismiss]);
  const Icon = ICONS[t.variant];
  return (
    <div className={`${styles.toast} ${styles[t.variant]}`} role="status">
      <span className={styles.icon} aria-hidden="true">
        <Icon size={18} />
      </span>
      <div className={styles.body}>
        {t.title && <div className={styles.title}>{t.title}</div>}
        <div className={styles.message}>{t.message}</div>
      </div>
      {t.action && <div className={styles.action}>{t.action}</div>}
      <button
        type="button"
        aria-label="Dismiss"
        className={styles.close}
        onClick={onDismiss}
      >
        <X size={20} aria-hidden="true" />
      </button>
    </div>
  );
}

